/**
 * Display formatters for the projects surfaces (PX-W6.3).
 *
 * The delivery-cut PHASE display names come from `@/modules/phases` — the
 * product phase ladder PX-W6.6 re-homed out of the demo module (the same
 * table the backend's `PhaseCrosswalk` quotes verbatim). The crosswalk ROW on
 * the overview still renders from the server projection; this map only names a
 * phase key for the ribbon/pill furniture. No client-side phase MATH lives
 * here.
 */

import { PHASES, type PhaseKey } from '@/modules/phases'
import type { StatusTone } from '@/components/px/StatusPill'

/**
 * Contract money UNREDACTED with thin-space thousands, for the admin
 * portfolio (I-3: the admin surface shows the figure verbatim, never
 * shortened) — 480_000 → "€ 480 000". Every gap is NON-BREAKING: U+00A0
 * after the euro sign and U+202F (a thin no-break space) between the
 * thousands, so a narrow column can never wrap the figure after the €
 * (TASK-17A2C062 fix cycle 1, F4). Never an ASCII space.
 */
export const eurThin = (amount: number): string =>
  `€\u00a0${Math.round(amount).toLocaleString('en-US').replace(/,/g, '\u202f')}`

/** Contract money in the board's shorthand: 480_000 → "€480k", 2_900_000 → "€2.9m". */
export const eurShort = (amount: number): string => {
  if (amount >= 1_000_000) {
    const millions = (amount / 1_000_000).toFixed(1).replace(/\.0$/, '')
    return `€${millions}m`
  }
  if (amount >= 1_000) return `€${Math.round(amount / 1_000)}k`
  return `€${amount}`
}

/** ISO day → "12 Nov 2026" (UTC, so fixed input renders fixed output). */
export const isoDayLabel = (iso?: string | null): string => {
  if (!iso) return '—'
  const date = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

/** Epoch millis → "4 Sep 2026, 14:05" (local, for audit-style stamps). */
export const epochLabel = (ms?: number | null): string => {
  if (ms === undefined || ms === null || ms <= 0) return '—'
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/** A delivery-cut phase key → its display name; unknown keys render verbatim. */
export const phaseDisplayName = (key?: string | null): string => {
  if (!key) return '—'
  return PHASES.find((phase) => phase.key === key)?.name ?? key
}

/**
 * A wire phase key → the ribbon's `PhaseKey`, or NULL when the key is absent
 * or not one of the seven (review D1). This is the TOTAL mapping at the
 * workspace's own boundary: `PhaseRibbon` and its `demo/architecture`
 * lookups are LOCKED furniture that THROWS on an unknown phase
 * ("No engagement phase realises delivery phase: …"), so the caller must
 * decide what renders when there is no phase yet — never cast an empty
 * string through the lookup.
 */
export const toRibbonPhase = (key?: string | null): PhaseKey | null =>
  PHASES.some((phase) => phase.key === key) ? (key as PhaseKey) : null

/** Server health string → pill tone. Unknown values render neutral, never invented. */
export const healthTone = (health?: string | null): StatusTone => {
  if (health === 'On track') return 'done'
  if (health === 'At risk') return 'review'
  if (health === 'Blocked') return 'blocked'
  return 'neutral'
}

/** Whether the health string carries the exception wash (row background). */
export const healthRowClass = (health?: string | null): string => {
  if (health === 'At risk') return 'bg-review-bg/60'
  if (health === 'Blocked') return 'bg-blocked-bg'
  return ''
}
